import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { type Lead, type LeadConversionMetrics } from "../lib/lead-types.ts";
import { getLeads, calculateConversionMetrics } from "../lib/lead-storage.ts";
import { LeadDirectory } from "../components/leads/LeadDirectory.tsx";
import { LeadOutreachComposer } from "../components/leads/LeadOutreachComposer.tsx";
import { LeadTemplatesCatalog } from "../components/leads/LeadTemplatesCatalog.tsx";
import { LeadHistorySection } from "../components/leads/LeadHistorySection.tsx";
import { LeadEditorModal } from "../components/leads/LeadEditorModal.tsx";

type ActiveTab = "directory" | "composer" | "templates" | "history";

export function LeadsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [metrics, setMetrics] = useState<LeadConversionMetrics>({
    totalLeads: 0,
    contactedCount: 0,
    repliedCount: 0,
    convertedCount: 0,
    notInterestedCount: 0,
    conversionRate: 0,
    contactedRate: 0,
    responseRate: 0,
  });

  const [selectedLeadId, setSelectedLeadId] = useState<string>("");
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);

  // Tab state from URL query
  const tabParam = searchParams.get("tab") as ActiveTab | null;
  const leadIdParam = searchParams.get("leadId");

  const activeTab: ActiveTab =
    tabParam === "composer" || tabParam === "templates" || tabParam === "history"
      ? tabParam
      : "directory";

  function refreshData() {
    const loadedLeads = getLeads();
    setLeads(loadedLeads);
    setMetrics(calculateConversionMetrics());
  }

  useEffect(() => {
    refreshData();
  }, []);

  useEffect(() => {
    if (leadIdParam) {
      setSelectedLeadId(leadIdParam);
    }
  }, [leadIdParam]);

  function setTab(tab: ActiveTab) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", tab);
      return next;
    });
  }

  function handleSelectLeadForOutreach(leadId: string) {
    setSelectedLeadId(leadId);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", "composer");
      next.set("leadId", leadId);
      return next;
    });
  }

  function handleUseTemplate(templateId: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", "composer");
      next.set("templateId", templateId);
      return next;
    });
  }

  function handleOpenAddModal() {
    setEditingLead(null);
    setIsEditorOpen(true);
  }

  function handleOpenEditModal(lead: Lead) {
    setEditingLead(lead);
    setIsEditorOpen(true);
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Leads &amp; Outreach</h1>
          <p className="muted">
            Prospect external apps, send manual 1-by-1 outreach &amp; follow-ups from{" "}
            <span className="mono">mail@currencycore.dev</span> (or{" "}
            <span className="mono">mail@currency-core.com</span>), and track app conversion.
          </p>
        </div>

        <div className="page-head-actions">
          <button type="button" className="btn btn-secondary" onClick={handleOpenAddModal}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Lead
          </button>

          {activeTab !== "composer" && (
            <button type="button" className="btn btn-primary" onClick={() => setTab("composer")}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M22 2L11 13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
              Compose Outreach
            </button>
          )}
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="email-tabs-nav">
        <button
          type="button"
          className={`email-nav-tab ${activeTab === "directory" ? "active" : ""}`}
          onClick={() => setTab("directory")}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          Leads Directory ({leads.length})
        </button>

        <button
          type="button"
          className={`email-nav-tab ${activeTab === "composer" ? "active" : ""}`}
          onClick={() => setTab("composer")}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M22 2L11 13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
          Outreach Composer (1-by-1)
        </button>

        <button
          type="button"
          className={`email-nav-tab ${activeTab === "templates" ? "active" : ""}`}
          onClick={() => setTab("templates")}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
          </svg>
          Outreach Templates
        </button>

        <button
          type="button"
          className={`email-nav-tab ${activeTab === "history" ? "active" : ""}`}
          onClick={() => setTab("history")}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          Sent History
        </button>
      </div>

      {/* Tab Panels */}
      <div className="lead-tab-panel mt-4">
        {activeTab === "directory" && (
          <LeadDirectory
            leads={leads}
            metrics={metrics}
            onRefresh={refreshData}
            onSelectLeadForOutreach={handleSelectLeadForOutreach}
            onOpenAddModal={handleOpenAddModal}
            onOpenEditModal={handleOpenEditModal}
          />
        )}

        {activeTab === "composer" && (
          <LeadOutreachComposer
            leads={leads}
            selectedLeadId={selectedLeadId}
            onLeadChange={setSelectedLeadId}
            onEmailSent={refreshData}
            onNavigateToDirectory={() => setTab("directory")}
          />
        )}

        {activeTab === "templates" && (
          <LeadTemplatesCatalog onUseTemplate={handleUseTemplate} />
        )}

        {activeTab === "history" && <LeadHistorySection />}
      </div>

      {/* Add / Edit Lead Modal */}
      <LeadEditorModal
        isOpen={isEditorOpen}
        lead={editingLead}
        onClose={() => setIsEditorOpen(false)}
        onSaved={refreshData}
      />
    </>
  );
}
